import React, { useCallback, useState } from 'react';
import {
    ActivityIndicator,
    Pressable,
    ScrollView,
    StyleSheet,
    Text,
    View,
} from 'react-native';
import { centralAPI } from '../../services/central-api';
import CustomButton from '../../components/CustomButton';
import { faCopy } from '@fortawesome/free-solid-svg-icons';
import Toast from 'react-native-toast-message';
import { translate } from '../../services/translate';

type LatencyTestResult = {
    samples: number[];
    backendSamples: number[];
    min: number;
    avg: number;
    max: number;
    jitter: number;
    backendAvg: number;
};

type RequestTestResult = {
    total: number;
    success: number;
    failed: number;
    successRate: number;
};

type DownloadTestResult = {
    bytes: number;
    ms: number;
    mbps: number;
};

type DiagnosticResult = {
    latency: LatencyTestResult;
    requests: RequestTestResult;
    download1Mb: DownloadTestResult;
    download10Mb: DownloadTestResult;
};

const PING_COUNT = 30;
const PACKET_TEST_COUNT = 50;

const sleep = (ms: number) =>
    new Promise(resolve => setTimeout(resolve, ms));

const formatMs = (value: number) =>
    `${Math.round(value)} ms`;

const formatMbps = (value: number) =>
    `${value.toFixed(2)} Mbps`;

const getJitterStatus = (jitter: number) => {
    if (jitter < 30) {
        return 'Estável';
    }

    if (jitter < 100) {
        return 'Oscilação moderada';
    }

    return 'Muito instável';
};

const getDiagnosis = (result: DiagnosticResult) => {
    const { latency, requests } = result;

    /*
     * Se o Express está processando rapidamente, mas o browser
     * está demorando muito, temos um forte indicativo de que
     * o problema está fora da aplicação.
     */
    if (
        latency.backendAvg < 50 &&
        latency.avg > 500
    ) {
        return {
            title: 'Possível problema de rede',
            description:
                'O servidor está processando as requisições rapidamente, mas o tempo percebido pelo navegador é elevado. Isso indica possível problema no caminho entre este computador e o servidor.',
        };
    }

    if (requests.successRate < 100) {
        return {
            title: 'Possível instabilidade de conexão',
            description:
                'Algumas requisições não foram concluídas. Isso pode indicar perda de pacotes, timeout, proxy, firewall ou instabilidade na conexão.',
        };
    }

    if (latency.jitter > 100) {
        return {
            title: 'Conexão instável',
            description:
                'A latência está oscilando significativamente entre as requisições.',
        };
    }

    if (latency.avg > 500) {
        return {
            title: 'Latência elevada',
            description:
                'A conexão até o servidor apresenta latência acima do esperado.',
        };
    }

    return {
        title: 'Conexão aparentemente saudável',
        description:
            'Não foram identificados problemas significativos nos testes realizados.',
    };
};

export default function ConnectionDiagnostics() {
    const [running, setRunning] = useState(false);
    const [progress, setProgress] = useState('');
    const [result, setResult] =
        useState<DiagnosticResult | null>(null);
    const [error, setError] = useState<string | null>(null);

    const copyDiagnostic = useCallback(async () => {
        if (!result) {
            return;
        }

        const diagnosis = getDiagnosis(result);

        const now = new Date();

        const browser = navigator.userAgent;
        const platform = navigator.platform;
        const language = navigator.language;

        const networkTime = Math.max(
            0,
            result.latency.avg -
            result.latency.backendAvg,
        );

        const text = [
            'SENTINELX - DIAGNÓSTICO DE CONEXÃO',
            '==================================',
            '',
            `Data: ${now.toLocaleString()}`,
            `Navegador: ${browser}`,
            `Plataforma: ${platform}`,
            `Idioma: ${language}`,
            '',
            'LATÊNCIA',
            '--------',
            `Mínima: ${formatMs(result.latency.min)}`,
            `Média: ${formatMs(result.latency.avg)}`,
            `Máxima: ${formatMs(result.latency.max)}`,
            `Jitter: ${formatMs(result.latency.jitter)}`,
            '',
            'SERVIDOR',
            '--------',
            `Backend médio: ${formatMs(
                result.latency.backendAvg,
            )}`,
            `Tempo de rede estimado: ${formatMs(
                networkTime,
            )}`,
            '',
            'ESTABILIDADE',
            '------------',
            `Requisições: ${result.requests.total}`,
            `Sucesso: ${result.requests.success}`,
            `Falhas: ${result.requests.failed}`,
            `Taxa de sucesso: ${result.requests.successRate.toFixed(
                1,
            )}%`,
            '',
            'DOWNLOAD',
            '--------',
            `1 MB: ${formatMbps(
                result.download1Mb.mbps,
            )} (${formatMs(
                result.download1Mb.ms,
            )})`,
            `10 MB: ${formatMbps(
                result.download10Mb.mbps,
            )} (${formatMs(
                result.download10Mb.ms,
            )})`,
            '',
            'DIAGNÓSTICO',
            '-----------',
            diagnosis.title,
            '',
            diagnosis.description,
            '',
            '==================================',
            `Horário UTC: ${now.toISOString()}`,
        ].join('\n');

        try {
            await navigator.clipboard.writeText(text);

            Toast.show({
                type: 'sentinelxSuccess',
                text1: translate('copiedToClipboard'),
            });
        } catch (error) {
            console.error(
                'Não foi possível copiar o diagnóstico.',
                error,
            );
        }
    }, [result]);

    const runLatencyTest =
        useCallback(async (): Promise<LatencyTestResult> => {
            const samples: number[] = [];
            const backendSamples: number[] = [];

            for (let i = 0; i < PING_COUNT; i++) {
                setProgress(`Teste de latência: ${i + 1}/${PING_COUNT}`);

                try {
                    const sample = await centralAPI.ping();

                    samples.push(sample.latency);
                    backendSamples.push(sample.backendTime);
                } catch {
                    // Uma falha não entra como latência.
                }

                await sleep(150);
            }

            if (!samples.length) {
                throw new Error('Nenhuma requisição de teste foi concluída.');
            }

            const min = Math.min(...samples);

            const max = Math.max(...samples);

            const avg = samples.reduce((sum, value) => sum + value, 0) / samples.length;

            let jitter = 0;

            if (samples.length > 1) {
                let totalDifference = 0;

                for (let i = 1; i < samples.length; i++) {
                    totalDifference += Math.abs(
                        samples[i] -
                        samples[i - 1],
                    );
                }

                jitter = totalDifference / (samples.length - 1);
            }

            const backendAvg =
                backendSamples.length
                    ? backendSamples.reduce(
                        (sum, value) =>
                            sum + value,
                        0,
                    ) /
                    backendSamples.length
                    : 0;

            return {
                samples,
                backendSamples,
                min,
                avg,
                max,
                jitter,
                backendAvg,
            };
        }, []);

    const runRequestTest =
        useCallback(async (): Promise<RequestTestResult> => {
            setProgress(
                `Teste de estabilidade: ${PACKET_TEST_COUNT} requisições`,
            );

            const results =
                await Promise.allSettled(
                    Array.from(
                        {
                            length: PACKET_TEST_COUNT,
                        },
                        () => centralAPI.ping(),
                    ),
                );

            const success =
                results.filter(
                    result => result.status === 'fulfilled',
                ).length;

            const failed = PACKET_TEST_COUNT - success;

            return {
                total: PACKET_TEST_COUNT,
                success,
                failed,
                successRate:
                    (success /
                        PACKET_TEST_COUNT) *
                    100,
            };
        }, []);

    const runTests = useCallback(async () => {
        if (running) {
            return;
        }

        setRunning(true);
        setError(null);
        setResult(null);

        try {
            setProgress('Iniciando diagnóstico...');

            const latency = await runLatencyTest();

            const requests = await runRequestTest();

            setProgress('Testando download de 1 MB...');

            const download1Mb = await centralAPI.diagnosticsDownload('1mb');

            setProgress('Testando download de 10 MB...');

            const download10Mb = await centralAPI.diagnosticsDownload('10mb');

            setProgress('Finalizando diagnóstico...');

            setResult({
                latency,
                requests,
                download1Mb,
                download10Mb,
            });
        } catch (err) {
            setError(
                err instanceof Error
                    ? err.message
                    : 'Erro desconhecido durante o diagnóstico.',
            );
        } finally {
            setRunning(false);
            setProgress('');
        }
    }, [
        running,
        runLatencyTest,
        runRequestTest,
    ]);

    const renderMetric = (
        label: string,
        value: string,
        description?: string,
    ) => (
        <View style={styles.metric}>
            <Text style={styles.metricLabel}>
                {label}
            </Text>

            <Text style={styles.metricValue}>
                {value}
            </Text>

            {description && (
                <Text style={styles.metricDescription}>
                    {description}
                </Text>
            )}
        </View>
    );

    return (
        <ScrollView
            style={styles.container}
            contentContainerStyle={styles.content}
        >
            <View style={styles.header}>
                <Text style={styles.title}>
                    Diagnóstico de conexão
                </Text>

                <Text style={styles.subtitle}>
                    Este teste mede a qualidade da
                    conexão entre este computador
                    e o servidor da aplicação.
                </Text>
            </View>

            <View style={styles.infoBox}>
                <Text style={styles.infoTitle}>
                    Importante
                </Text>

                <Text style={styles.infoText}>
                    O teste de velocidade da sua
                    internet mede a velocidade até
                    servidores específicos. Este
                    diagnóstico mede diretamente a
                    conexão com a nossa plataforma.
                </Text>
            </View>

            <Pressable
                onPress={runTests}
                disabled={running}
                style={({ pressed }) => [
                    styles.button,
                    running &&
                    styles.buttonDisabled,
                    pressed &&
                    !running &&
                    styles.buttonPressed,
                ]}
            >
                {running ? (
                    <ActivityIndicator color="#fff" />
                ) : (
                    <Text style={styles.buttonText}>
                        Iniciar diagnóstico
                    </Text>
                )}
            </Pressable>

            {progress && (
                <View style={styles.progressContainer}>
                    <ActivityIndicator />
                    <Text style={styles.progressText}>
                        {progress}
                    </Text>
                </View>
            )}

            {error && (
                <View style={styles.errorBox}>
                    <Text style={styles.errorTitle}>
                        Não foi possível concluir
                        o teste
                    </Text>

                    <Text style={styles.errorText}>
                        {error}
                    </Text>
                </View>
            )}

            {result && (
                <>
                    <View style={styles.diagnosisBox}>
                        <Text style={styles.diagnosisTitle}>
                            {getDiagnosis(result).title}
                        </Text>

                        <Text style={styles.diagnosisText}>
                            {getDiagnosis(result).description}
                        </Text>
                    </View>


                    <CustomButton
                        icon={faCopy}
                        onPress={async () => {
                            await copyDiagnostic();
                        }}
                        type='secondary'
                        text={translate('copyToClipboard')}
                    />

                    <Text style={styles.sectionTitle}>
                        Latência
                    </Text>

                    <View style={styles.grid}>
                        {renderMetric('Mínima', formatMs(result.latency.min))}

                        {renderMetric('Média', formatMs(result.latency.avg))}

                        {renderMetric('Máxima', formatMs(result.latency.max))}

                        {renderMetric(
                            'Jitter',
                            formatMs(result.latency.jitter),
                            getJitterStatus(result.latency.jitter),
                        )}

                        {renderMetric(
                            'Backend',
                            formatMs(result.latency.backendAvg),
                            'Tempo médio de processamento no servidor',
                        )}
                    </View>

                    <View style={styles.comparisonBox}>
                        <Text style={styles.comparisonTitle}>
                            Onde está o tempo?
                        </Text>

                        <Text style={styles.comparisonText}>
                            Browser → servidor
                        </Text>

                        <Text style={styles.comparisonValue} >
                            {formatMs(result.latency.avg)}
                        </Text>

                        <Text style={styles.comparisonText}>
                            Processamento do backend
                        </Text>

                        <Text style={styles.comparisonValue}>
                            {formatMs(result.latency.backendAvg)}
                        </Text>

                        <Text style={styles.comparisonHint}>
                            Diferença aproximada:
                            {' '}
                            {formatMs(Math.max(0, result.latency.avg - result.latency.backendAvg))}
                        </Text>
                    </View>

                    <Text style={styles.sectionTitle}>
                        Estabilidade
                    </Text>

                    <View style={styles.grid}>
                        {renderMetric('Requisições', `${result.requests.success}/${result.requests.total}`)}

                        {renderMetric('Sucesso', `${result.requests.successRate.toFixed(1)}%`)}

                        {renderMetric('Falhas', `${result.requests.failed}`)}
                    </View>

                    <Text style={styles.sectionTitle}>
                        Download
                    </Text>

                    <View style={styles.grid}>
                        {renderMetric(
                            '1 MB',
                            formatMbps(result.download1Mb.mbps),
                            formatMs(result.download1Mb.ms),
                        )}

                        {renderMetric(
                            '10 MB',
                            formatMbps(result.download10Mb.mbps),
                            formatMs(result.download10Mb.ms),
                        )}
                    </View>

                    <View style={styles.environmentBox}>
                        <Text style={styles.sectionTitle}>
                            Informações do ambiente
                        </Text>

                        {renderMetric('Navegador', navigator.userAgent)}

                        {renderMetric('Plataforma', navigator.platform)}

                        {renderMetric('Idioma', navigator.language)}

                        {renderMetric('CPU lógica', String(navigator.hardwareConcurrency ?? 'N/A'))}
                    </View>

                    <Text style={styles.footer}>
                        Diagnóstico realizado em{' '}{new Date().toLocaleString()}
                    </Text>
                </>
            )}
        </ScrollView>
    );
}

const styles = StyleSheet.create({
    container: {
        flex: 1,
        backgroundColor: '#f5f6f8',
    },

    content: {
        width: '100%',
        maxWidth: 900,
        alignSelf: 'center',
        padding: 32,
        paddingBottom: 80,
    },

    header: {
        marginBottom: 24,
    },

    title: {
        fontSize: 30,
        fontWeight: '700',
        color: '#17181a',
        marginBottom: 8,
    },

    subtitle: {
        fontSize: 16,
        lineHeight: 24,
        color: '#656b73',
    },

    infoBox: {
        padding: 18,
        borderRadius: 10,
        backgroundColor: '#e9f1ff',
        marginBottom: 20,
    },

    infoTitle: {
        fontSize: 15,
        fontWeight: '700',
        marginBottom: 6,
        color: '#1d4ed8',
    },

    infoText: {
        fontSize: 14,
        lineHeight: 21,
        color: '#334155',
    },

    button: {
        height: 52,
        borderRadius: 8,
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#2563eb',
        marginBottom: 20,
    },

    buttonDisabled: {
        opacity: 0.6,
    },

    buttonPressed: {
        opacity: 0.8,
    },

    buttonText: {
        color: '#fff',
        fontSize: 16,
        fontWeight: '700',
    },

    progressContainer: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 12,
        padding: 16,
        backgroundColor: '#fff',
        borderRadius: 8,
        marginBottom: 20,
    },

    progressText: {
        color: '#444',
        fontSize: 14,
    },

    diagnosisBox: {
        backgroundColor: '#fff',
        borderRadius: 12,
        padding: 20,
        marginBottom: 28,
    },

    diagnosisTitle: {
        fontSize: 20,
        fontWeight: '700',
        marginBottom: 8,
        color: '#17181a',
    },

    diagnosisText: {
        fontSize: 15,
        lineHeight: 22,
        color: '#555',
    },

    sectionTitle: {
        fontSize: 19,
        fontWeight: '700',
        color: '#17181a',
        marginBottom: 12,
    },

    grid: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: 12,
        marginBottom: 28,
    },

    metric: {
        flexGrow: 1,
        flexBasis: 160,
        backgroundColor: '#fff',
        borderRadius: 10,
        padding: 18,
        minHeight: 100,
    },

    metricLabel: {
        fontSize: 13,
        color: '#737981',
        marginBottom: 8,
    },

    metricValue: {
        fontSize: 22,
        fontWeight: '700',
        color: '#17181a',
    },

    metricDescription: {
        fontSize: 12,
        color: '#777',
        marginTop: 5,
    },

    comparisonBox: {
        backgroundColor: '#fff',
        borderRadius: 12,
        padding: 20,
        marginBottom: 28,
    },

    comparisonTitle: {
        fontSize: 18,
        fontWeight: '700',
        marginBottom: 16,
        color: '#17181a',
    },

    comparisonText: {
        fontSize: 13,
        color: '#777',
        marginTop: 8,
    },

    comparisonValue: {
        fontSize: 24,
        fontWeight: '700',
        color: '#17181a',
    },

    comparisonHint: {
        fontSize: 14,
        color: '#555',
        marginTop: 14,
    },

    environmentBox: {
        marginTop: 4,
    },

    footer: {
        fontSize: 12,
        color: '#999',
        marginTop: 20,
    },

    errorBox: {
        padding: 18,
        borderRadius: 10,
        backgroundColor: '#fee2e2',
        marginBottom: 20,
    },

    errorTitle: {
        fontWeight: '700',
        color: '#991b1b',
        marginBottom: 6,
    },

    errorText: {
        color: '#7f1d1d',
    },
});
